import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { ensurePermission, getReminderConfig } from "../lib/reminders";
import { subscribeToPush, isPushSupported, getPushStatus } from "../lib/push";
import { toast } from "sonner";

const DISMISS_KEY = "haccp_reminder_banner_dismissed_v1";
const ENABLED_KEY = "haccp_push_enabled_v1";

export default function ReminderBanner() {
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!("Notification" in window)) return;
        if (localStorage.getItem(DISMISS_KEY) || localStorage.getItem(ENABLED_KEY)) return;
        const cfg = getReminderConfig();
        if (!cfg.enabled || Notification.permission === "denied") return;
        let alive = true;
        getPushStatus()
            .then((st) => {
                if (!alive) return;
                if (st?.subscribed) { localStorage.setItem(ENABLED_KEY, "1"); return; }
                if (Notification.permission === "default" || !st?.subscribed) setShow(true);
            })
            .catch(() => { if (alive && Notification.permission === "default") setShow(true); });
        return () => { alive = false; };
    }, []);

    async function enable() {
        setBusy(true);
        const res = await ensurePermission();
        if (res === "granted") {
            try {
                if (isPushSupported()) {
                    const cfg = getReminderConfig();
                    await subscribeToPush(cfg.times);
                    localStorage.setItem(ENABLED_KEY, "1");
                    toast.success("Notifiche push attivate", { description: "Riceverai i promemoria anche a app chiusa." });
                } else {
                    localStorage.setItem(ENABLED_KEY, "1");
                    toast.success("Notifiche attivate (solo con app aperta)");
                }
            } catch (e) {
                toast.error("Errore attivazione push", { description: String(e?.message || e) });
            }
        } else {
            localStorage.setItem(DISMISS_KEY, "1");
        }
        setBusy(false);
        setShow(false);
    }

    function dismiss() {
        localStorage.setItem(DISMISS_KEY, "1");
        setShow(false);
    }

    if (!show) return null;

    const cfg = getReminderConfig();

    return (
        <div
            className="mx-4 sm:mx-6 lg:mx-8 mb-4 card-soft p-4 flex items-start gap-3 bg-[#FFF8EE] border-[#E9A846]/40"
            data-testid="reminder-banner"
        >
            <div className="w-10 h-10 rounded-xl bg-[#E9A846]/20 text-[#A87214] grid place-items-center shrink-0">
                <Bell size={18} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-forest-dark text-sm">
                    Attiva i promemoria giornalieri
                </p>
                <p className="text-xs text-stone-600 mt-0.5">
                    Riceverai una notifica alle <strong>{cfg.times.join(" e ")}</strong> per ricordarti la checklist di pulizia e igiene.
                </p>
                <div className="mt-2 flex gap-2">
                    <button
                        onClick={enable}
                        disabled={busy}
                        className="btn-primary text-xs"
                        data-testid="enable-notifications"
                    >
                        Attiva notifiche
                    </button>
                    <button
                        onClick={dismiss}
                        className="btn-ghost text-xs"
                        data-testid="dismiss-notifications"
                    >
                        Non ora
                    </button>
                </div>
            </div>
            <button onClick={dismiss} className="text-stone-400 hover:text-stone-600 p-1" aria-label="Chiudi">
                <X size={16} />
            </button>
        </div>
    );
}
